import { useState } from 'react';
import { authAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ShieldCheck, ShieldOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

// Additive account/security modal — 2FA is optional and scoped to admin/manager
// (see backend/routes/auth.py /2fa/setup). Triggered from the existing
// sidebar user block, no new nav item or page.
export default function AccountSecurityDialog({ open, onClose }) {
  const { user } = useAuth();
  const [step, setStep] = useState('idle'); // idle | setup | verifying
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const totpEnabled = user?.totp_enabled;
  const canUse2FA = user?.role === 'admin' || user?.role === 'manager';

  const handleStartSetup = async () => {
    setLoading(true);
    try {
      const res = await authAPI.setup2FA();
      setSetupData(res.data);
      setStep('setup');
    } catch (e) { toast.error(e.response?.data?.detail || '2FA-Setup fehlgeschlagen'); }
    finally { setLoading(false); }
  };

  const handleVerify = async () => {
    if (!code.trim()) return;
    setLoading(true);
    try {
      await authAPI.verify2FA(code.trim());
      toast.success('2FA aktiviert');
      setStep('idle'); setSetupData(null); setCode('');
      onClose(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Ungültiger Code'); }
    finally { setLoading(false); }
  };

  const handleDisable = async () => {
    setLoading(true);
    try {
      await authAPI.disable2FA();
      toast.success('2FA deaktiviert');
      onClose(true);
    } catch { toast.error('Deaktivieren fehlgeschlagen'); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="w-4 h-4" />Konto &amp; Sicherheit
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="text-sm">
            <p className="font-medium">{user?.name}</p>
            <p className="text-xs text-muted-foreground">{user?.email}</p>
          </div>

          {!canUse2FA ? (
            <p className="text-xs text-muted-foreground">
              Zwei-Faktor-Authentifizierung ist aktuell nur für Admin- und Manager-Rollen verfügbar.
            </p>
          ) : step === 'idle' ? (
            <div className="flex items-center justify-between border rounded-lg p-3">
              <div>
                <p className="text-sm font-medium">Zwei-Faktor-Authentifizierung (2FA)</p>
                <Badge variant={totpEnabled ? 'default' : 'secondary'} className="text-[10px] mt-1">
                  {totpEnabled ? 'Aktiviert' : 'Deaktiviert'}
                </Badge>
              </div>
              {totpEnabled ? (
                <Button size="sm" variant="destructive" onClick={handleDisable} disabled={loading} className="gap-1.5">
                  <ShieldOff className="w-3.5 h-3.5" />Deaktivieren
                </Button>
              ) : (
                <Button size="sm" onClick={handleStartSetup} disabled={loading} className="gap-1.5" data-testid="start-2fa-setup-btn">
                  {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}Einrichten
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Mit einer Authenticator-App (z.B. Google Authenticator) scannen oder den Code manuell eingeben:
              </p>
              {setupData?.qr_code_png_base64 && (
                <img
                  src={`data:image/png;base64,${setupData.qr_code_png_base64}`}
                  alt="2FA QR-Code"
                  className="mx-auto border rounded-lg"
                  data-testid="2fa-qr-code"
                />
              )}
              <code className="block text-xs font-mono bg-muted px-3 py-2 rounded text-center break-all">{setupData?.secret}</code>
              <div>
                <Label className="text-xs">Code aus der App eingeben zur Bestätigung</Label>
                <Input value={code} onChange={e => setCode(e.target.value)} placeholder="123456" className="h-9 mono text-center" maxLength={6} data-testid="2fa-verify-input" />
              </div>
              <Button className="w-full" onClick={handleVerify} disabled={loading || code.length < 6} data-testid="2fa-verify-btn">
                {loading ? 'Prüfe...' : 'Bestätigen & aktivieren'}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
